import {action, observable, runInAction} from 'mobx';
import {observer, PropTypes as MobxPropTypes} from 'mobx-react';
import React, {Component} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlus} from '@fortawesome/free-solid-svg-icons';

import LoadingIcon from '../../common/loadingIcon/LoadingIcon';
import inject from '../../hoc/injectHoc';
import ConfirmModal from '../../modals/confirm/ConfirmModal';
import SignAddEditModal from '../../modals/signAddEdit/SignAddEditModal';
import {STATE_PENDING} from '../../../constants/asyncConstants';

import './adminCompanySignsPage.scss';

const signFieldMap = [
  {label: 'Manufacturer', field: 'manufacturer'},
  {label: 'AspectRatio', field: 'aspectRatio'},
  {label: 'Pitch', field: 'pitch', postfix: 'mm'},
];

/**
 * The AdminCompanySignsPage component.
 */
export class AdminCompanySignsPage extends Component {
  /**
   * Whether or not the add sign modal should be open.
   *
   * @type {boolean}
   */
  @observable shouldAddSign = false;

  /**
   * The sign object for the sign to be edited.
   * This will open the edit sign modal.
   *
   * @type {?{}}
   */
  @observable editSign = null;

  /**
   * The sign object for the sign to be deleted.
   * This will open the delete sign confirm modal.
   *
   * @type {?{}}
   */
  @observable deleteSign = null;

  /**
   * Triggered when the component is first mounted to the page.
   */
  componentDidMount() {
    const {apiAdminCompanySignGetAllStore, apiCompanyGetOneStore, routerStore} = this.props;

    const companyId = this.getCompanyId();

    apiCompanyGetOneStore.refresh(companyId);
    apiAdminCompanySignGetAllStore.refresh(companyId);

    const addSignQueryParam = routerStore.getQuery('addSign');
    if (addSignQueryParam) {
      runInAction('presetAddSign', () => {
        this.shouldAddSign = true;
      });
    }

    apiAdminCompanySignGetAllStore.getPromise(companyId).then((signs) => {
      const editSignIdQueryParam = routerStore.getQuery('editSignId');
      if (signs && !Number.isNaN(editSignIdQueryParam) && signs.some((sign) => sign.id === Number(editSignIdQueryParam))) {
        runInAction('presetEditSign', () => {
          const matchingSign = signs.find((sign) => sign.id === Number(editSignIdQueryParam));
          this.editSign = matchingSign;
        });
      }

      const deleteSignIdQueryParam = routerStore.getQuery('deleteSignId');
      if (signs && !Number.isNaN(deleteSignIdQueryParam) && signs.some((sign) => sign.id === Number(deleteSignIdQueryParam))) {
        runInAction('presetEditSign', () => {
          const matchingSign = signs.find((sign) => sign.id === Number(deleteSignIdQueryParam));
          this.deleteSign = matchingSign;
        });
      }
    });
  }

  /**
   * Gets the company id from the url params.
   *
   * @param {{}=} props
   * @returns {number|undefined}
   */
  getCompanyId = (props) => {
    const {
      /** @type {RouterStore} */ routerStore
    } = (props || this.props);

    return routerStore.getParam('companyId');
  };

  /**
   * Triggered when the add sign button is clicked.
   */
  @action onAddSign = () => {
    this.shouldAddSign = true;
    this.editSign = null;
    this.deleteSign = null;
  };

  /**
   * Triggered when the add sign modal is closed.
   */
  @action afterAddSign = () => {
    const {routerStore} = this.props;

    this.shouldAddSign = false;

    const redirectUrlQueryParam = routerStore.getQuery('redirectUrl');
    if (redirectUrlQueryParam) {
      window.location.href = redirectUrlQueryParam;
    }
  };

  /**
   * Triggered when the edit sign button is clicked.
   *
   * @param {{id: number}} sign
   * @returns {function}
   */
  onEditSign = (sign) => {
    return action('openEditSignModal', () => {
      this.editSign = sign;
      this.shouldAddSign = false;
      this.deleteSign = null;
    });
  };

  /**
   * Triggered when the edit sign modal is closed.
   */
  @action afterEditSign = () => {
    const {routerStore} = this.props;

    this.editSign = null;

    const redirectUrlQueryParam = routerStore.getQuery('redirectUrl');
    if (redirectUrlQueryParam) {
      window.location.href = redirectUrlQueryParam;
    }
  };

  /**
   * Triggered when the delete sign button is clicked.
   *
   * @param {{id: number}} sign
   * @returns {function}
   */
  onDeleteSign = (sign) => {
    return action('openDeleteSignConfirm', () => {
      this.deleteSign = sign;
      this.shouldAddSign = false;
      this.editSign = null;
    });
  };

  /**
   * Triggered when the user confirms the sign should be deleted.
   *
   * @param {boolean} wasConfirmed
   */
  @action afterConfirmDelete = (wasConfirmed) => {
    const {routerStore} = this.props;
    const sign = this.deleteSign;

    this.deleteSign = null;

    if (!wasConfirmed) {
      const redirectUrlQueryParam = routerStore.getQuery('redirectUrl');
      if (redirectUrlQueryParam) {
        window.location.href = redirectUrlQueryParam;
      }
      return;
    }

    const {apiSignDeleteStore} = this.props;
    const companyId = this.getCompanyId();

    apiSignDeleteStore.makeRequest(sign.id, companyId);
    apiSignDeleteStore.getPromise(sign.id).then(() => {
      const redirectUrlQueryParam = routerStore.getQuery('redirectUrl');
      if (redirectUrlQueryParam) {
        window.location.href = redirectUrlQueryParam;
      }
    });
  };

  /**
   * Parses the signs into the cards.
   *
   * @param {Array<{}>} signs
   * @returns {Array<{}>}
   */
  parseSignsToCard = (signs) => {
    const {apiSignDeleteStore} = this.props;

    return signs.map((sign) => {
      const deleteState = apiSignDeleteStore.getState(sign.id);
      const deleteIsPending = (deleteState === STATE_PENDING);

      let dimensions = null;
      if (sign.width && sign.height) {
        dimensions = `${sign.width} x ${sign.height}`;
      } else if (sign.width) {
        dimensions = `Width: ${sign.width}px`;
      } else if (sign.height) {
        dimensions = `Height: ${sign.height}px`;
      }

      return (
        <div className="card" key={sign.id}>
          <div className="card-body">
            <h5 className="card-title">{sign.name}</h5>

            {(dimensions) && (
              <h6 className="card-subtitle mb-2 text-muted">{dimensions}</h6>
            )}

            <ul className="card-text">
              {signFieldMap.map(({label, field, postfix}) => (
                <li key={field}>{label}: {sign[field]}{postfix || ''}</li>
              ))}
            </ul>

            <button
              type="button"
              className="btn btn-sm btn-primary card-link"
              onClick={this.onEditSign(sign)}
            >Edit</button>

            <button
              type="button"
              className="btn btn-sm btn-danger card-link"
              onClick={this.onDeleteSign(sign)}
              disabled={deleteIsPending}
            >{(deleteIsPending) ? 'Deleting...' : 'Delete'}</button>
          </div>
        </div>
      );
    });
  };

  /**
   * Renders the signs.
   *
   * @param {{}} company
   * @returns {{}}
   */
  renderSigns = (company) => {
    const {apiAdminCompanySignGetAllStore} = this.props;

    return apiAdminCompanySignGetAllStore.case(company.id, {
      pre: () => <LoadingIcon />,
      pending: () => <LoadingIcon />,
      rejected: () => (
        <div className="alert alert-warning">
          The list of signs could not be loaded.
        </div>
      ),
      fulfilled: (signs) => (
        <div className="card-deck">
          {this.parseSignsToCard(signs)}

          <div className="card add-new-card">
            <div className="card-body">
              <button
                type="button"
                className="btn add-new-button"
                onClick={this.onAddSign}
              >
                <FontAwesomeIcon
                  icon={faPlus}
                  size="3x"
                />
              </button>
            </div>
          </div>
        </div>
      ),
    });
  };

  /**
   * Renders the component.
   *
   * @returns {{}}
   */
  render() {
    const {apiCompanyGetOneStore} = this.props;

    const companyId = this.getCompanyId();
    const company = apiCompanyGetOneStore.getFulfilled(companyId);

    return (
      <div id="admin-company-signs-page" className="system-page full-height container-fluid">
        <div className="container">
          <h2 className="signs-header">Company Signs{(company) ? ` for ${company.name}` : ''}</h2>

          {apiCompanyGetOneStore.case(companyId, {
            pre: () => <LoadingIcon />,
            pending: () => <LoadingIcon />,
            rejected: () => (
              <div className="alert alert-warning">
                The list of signs could not be loaded.
              </div>
            ),
            fulfilled: (foundCompany) => this.renderSigns(foundCompany),
          })}
        </div>

        {(this.deleteSign) && (
          <ConfirmModal
            isOpen={true}
            confirmText={`Are you sure you want to delete ${this.deleteSign.name}?`}
            onComplete={this.afterConfirmDelete}
            isYesNo={true}
          />
        )}

        {(this.shouldAddSign) && (
          <SignAddEditModal
            isOpen={true}
            isAdmin={true}
            companyId={companyId}
            onComplete={this.afterAddSign}
          />
        )}

        {(this.editSign) && (
          <SignAddEditModal
            isOpen={true}
            sign={this.editSign}
            isAdmin={true}
            companyId={companyId}
            onComplete={this.afterEditSign}
          />
        )}
      </div>
    );
  }
}

AdminCompanySignsPage.propTypes = {
  apiAdminCompanySignGetAllStore: MobxPropTypes.observableObject,
  apiCompanyGetOneStore: MobxPropTypes.observableObject,
  apiSignDeleteStore: MobxPropTypes.observableObject,
  routerStore: MobxPropTypes.observableObject,
};

AdminCompanySignsPage.wrappedComponent = {};
AdminCompanySignsPage.wrappedComponent.propTypes = {
  apiAdminCompanySignGetAllStore: MobxPropTypes.observableObject.isRequired,
  apiCompanyGetOneStore: MobxPropTypes.observableObject.isRequired,
  apiSignDeleteStore: MobxPropTypes.observableObject.isRequired,
  routerStore: MobxPropTypes.observableObject.isRequired,
};

export default inject(AdminCompanySignsPage)(
  observer(AdminCompanySignsPage)
);
